import {
  SplitDropDown,
  NavPills,
  DashboardTopBar,
  ItemContentArea,
  RelatedPaymentsGrid,
  RelatedInvoicesGrid,
  RelatedOrdersGrid,
  RelatedPaymentMethodGrid,
  NewSubscriptionPopUp,
} from "../../../shared/components";
import CancelSubscriptionPopUp from "./CancelSubscriptionPopUp";
import { observer } from "mobx-react";
import React, { FC, useEffect, useState } from "react";
import DetailSection from "./DetailSection";
// import DeleteIcon from '../../../../../../images/products/trash-blue.svg';
import CancelIcon from "../../../../../../images/products/cross-blue.svg";
import PauseIcon from "../../../../../../images/common/pause-royal-blue.svg";
import ResumeIcon from "../../../../../../images/common/resume-button.svg";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import { NavigateFunction, Params } from "react-router-dom";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  NavigationButtons,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  ItemsStore,
  RolesAndPermissionStore,
  SubscriptionStore,
} from "../../../shared/store";
import {  SubscriptionService } from "../../../shared/services"; 
import {
  RelatedInvoicesGridModel,
  SelectedItemModel,
  SubscriptionDetailPageModel,
  SubscriptionModel,
} from "../../../shared/models";
import {
  InvoiceStatus,
  NewPaymentRelatedSource,
  SubscriptionStatus,
} from "../../../shared/enums";
import {
  ItemDetailMainWrapper,
  MainOuterCard,
  ProductDetailWrapper,
  SubscriptionStylesWrapper,
} from "../../../shared/styles";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";
import ResumeSubscriptionPopup from "../../../shared/components/ResumeSubscriptionPopup";

type IProps = {
  subscriptionStore: SubscriptionStore;
  subscriptionId: string;
  params: Readonly<Params<string>>;
  itemStore: ItemsStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};

type SplitButtonProps = {
  icon: string;
  label: string;
  value: string;
  onClick: () => void;
};

const SubscriptionDetailMain: FC<IProps> = ({
  params,
  subscriptionStore,
  itemStore,
  navigate,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [activeNavPill, setActiveNavPill] = useState("details");
  const [showLoader, setLoader] = useState(true);
  const [isEditPopUpOpen, setIsEditPopUpOpen] = useState(false);
  const [showCancelPopUp, setShowCancelPopUp] = useState(false);
  const [showPausePopUp, setShowPausePopUp] = useState(false);
  const [showResumePopUp, setShowResumePopUp] = useState(false);

  function subscriptionId() {
    const id = params.id || "";
    return UtilityMethods.isValidObjectId(id) ? id : "";
  }

  useEffect(() => {
    onRefresh();
    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, [subscriptionId()]);

  function onRefresh() {
    setLoader(true);
    SubscriptionService.getSubscription(
      AuthService.authData.accountId,
      subscriptionId(),
      subscriptionStore.searchValue
    ).subscribe({
      next: (response) => {
        setLoader(false);
        const details = SubscriptionDetailPageModel.deserializeAPIModel(
          response.result
        );
        subscriptionStore.updateDetailPage(details);
        itemStore?.setBreadCrumbData(
          new SelectedItemModel({
            route: "/app/subscriptions",
            title: details.detail.description,
            icon: "",
          })
        );
      },
      error: () => {
        ToastService.showError("Error in getting subscription");
      },
    });
  }

  const onClose = (refresh: boolean) => {
    setIsEditPopUpOpen(false);
    if (refresh) {
      onRefresh();
    }
  };

  const onClosePause = (refresh: boolean) => {
    setShowPausePopUp(false);
    if (refresh) {
      onRefresh();
    }
  };

  const onCloseResume = (refresh: boolean) => {
    setShowResumePopUp(false);
    if (refresh) {
      onRefresh();
    }
  };

  const onCloseCancel = (refresh: boolean) => {
    setShowCancelPopUp(false);
    if (refresh) {
      onRefresh();
    }
  };

  if (subscriptionStore.detailPage === null) return null;

  const navPills = () => {
    return (
      <FlexContainer alignItems="center" padding="0px 0px 30px">
        <CustomLabel color="#2C3E50" fontSize="30px" fontWeight="600">
          Subscription
        </CustomLabel>
        <div className="flex-auto-grow">
          {false && (
            <NavPills
              defaultActiveKey="details"
              pills={[
                {
                  key: "details",
                  title: "DETAILS",
                },
                {
                  key: "activity",
                  title: "ACTIVITY",
                },
              ]}
              onSelect={(key) => setActiveNavPill(key)}
            />
          )}
        </div>
      </FlexContainer>
    );
  };

  const actionDropdown = () => {
    const options: SplitButtonProps[] = [
      {
        icon: EditIcon,
        label: "Edit",
        value: "edit",
        onClick: () => {
          if (!subscriptionId()) return;
          if (!rolesAndPermissionStore.hasManagePermission("Edit Subscription"))
            return;
          setIsEditPopUpOpen(true);
        },
      },
    ];

    if (
      subscriptionStore.detailPage?.detail.status !==
      SubscriptionStatus.Cancelled
    ) {
      options.push({
        icon: CancelIcon,
        label: "Cancel",
        value: "cancel",
        onClick: () => {
          if (!subscriptionId()) return;
          if (
            !rolesAndPermissionStore.hasManagePermission("Cancel Subscription")
          )
            return;
          setShowCancelPopUp(true);
        },
      });
    }

    // F07: Pause only for a running subscription (it was offered on Cancelled ones too).
    const status = subscriptionStore.detailPage?.detail.status;
    if (
      status === ("Active" as keyof typeof SubscriptionStatus) ||
      status === ("PastDue" as keyof typeof SubscriptionStatus)
    ) {
      options.push({
        icon: PauseIcon,
        label: "Pause",
        value: "pause",
        onClick: () => {
          if (!subscriptionId()) return;
          if (
            !rolesAndPermissionStore.hasManagePermission("Pause Subscription")
          )
            return;
          setShowPausePopUp(true);
        },
      });
    }

    if (
      subscriptionStore.detailPage?.detail.status === SubscriptionStatus.Paused
    ) {
      // Resume stays hidden (owner decision) until the resume API flow is reviewed; see the Money security
      // findings list, item RES-1.
      // options.push({
      //   icon: ResumeIcon,
      //   label: "Resume",
      //   value: "resume",
      //   onClick: () => {
      //     if (
      //       !subscriptionId() ||
      //       !rolesAndPermissionStore.hasManagePermission("Resume Subscription")
      //     )
      //       return;
      //     setShowResumePopUp(true);
      //   },
      // });
    }

    if (
      subscriptionStore.detailPage?.detail.status ===
      SubscriptionStatus.Completed
    ) {
      return null;
    }

    return (
      <SplitDropDown
        label="Edit"
        onClick={() => {
          if (!subscriptionId()) return;
          if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
          setIsEditPopUpOpen(true);
        }}
        className="edit-dropdown-item"
        options={options}
      />
    );
  };

  const showInvoices = () => {
    if (!subscriptionStore.detailPage) return false;
    if (subscriptionStore.detailPage?.invoices?.length > 0) {
      return subscriptionStore.detailPage.invoices.some(
        (invoice: RelatedInvoicesGridModel) =>
          invoice.status === ("PastDue" as keyof typeof InvoiceStatus)
      );
    }
    return false;
  };

  return (
    <>
      <DashboardTopBar
        itemStore={itemStore}
        rolesAndPermissionStore={rolesAndPermissionStore}
        rightContent={
          <>
            <NavigationButtons
              currentIndex={subscriptionStore.detailPage.index}
              totalItems={subscriptionStore.detailPage.totalSubscriptions}
              previousRecordId={subscriptionStore.detailPage.previousRecordId}
              nextRecordId={subscriptionStore.detailPage.nextRecordId}
              onNavigate={(id) => navigate(`/app/subscriptions/detail/${id}`)}
            />
            {actionDropdown()}
          </>
        }
        merchantAccountStore={merchantAccountStore}
      />
      <ItemContentArea
        showLoader={showLoader}
        itemStore={itemStore}
        showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        node={
          <ItemDetailMainWrapper>
            <ProductDetailWrapper>
              {" "}
              <ItemDetailMainWrapper>
                <SubscriptionStylesWrapper>
                  <MainOuterCard>
                    {" "}
                    {navPills()}
                    {/* <ItemDetailLeftPanel> */}
                    {activeNavPill === "details" && (
                      <DetailSection
                        lastInvoice={subscriptionStore.detailPage.lastInvoice}
                        data={subscriptionStore.detailPage.detail}
                      />
                    )}
                    {/* </ItemDetailLeftPanel> */}
                  </MainOuterCard>
                  {activeNavPill === "details" && (
                    <>
                      <RelatedOrdersGrid
                        orders={subscriptionStore.detailPage.orders}
                        countriesWithState={itemStore.countryStore.countriesWithState}
                      />
                      <RelatedInvoicesGrid
                        invoices={subscriptionStore.detailPage.invoices}
                        contact={subscriptionStore.detailPage.detail.contact}
                        countriesWithState={itemStore.countryStore.countriesWithState}
                        onUpdate={() => onRefresh()}
                        source={NewPaymentRelatedSource.Subscription}
                        sourceId={subscriptionStore.detailPage.detail.id}
                        rolesAndPermissionStore={rolesAndPermissionStore}
                      />
                      <RelatedPaymentsGrid
                        payments={subscriptionStore.detailPage.payments}
                        source={NewPaymentRelatedSource.Subscription}
                        sourceId={subscriptionStore.detailPage.detail.id}
                        contact={subscriptionStore.detailPage.detail.contact}
                        rolesAndPermissionStore={rolesAndPermissionStore}
                        countriesWithState={itemStore.countryStore.countriesWithState}
                        onUpdate={() => onRefresh()}
                      />
                      <RelatedPaymentMethodGrid
                        paymentMethods={
                          subscriptionStore.detailPage.paymentMethods
                        }
                        onUpdate={() => onRefresh()}
                        contact={subscriptionStore.detailPage.detail.contact}
                        rolesAndPermissionStore={rolesAndPermissionStore}
                        source={NewPaymentRelatedSource.Subscription}
                        sourceId={subscriptionStore.detailPage.detail.id}
                        countriesWithState={itemStore.countryStore.countriesWithState}
                      />
                    </>
                  )}
                </SubscriptionStylesWrapper>
              </ItemDetailMainWrapper>
            </ProductDetailWrapper>
          </ItemDetailMainWrapper>
        }
      />
      {isEditPopUpOpen && (
        <NewSubscriptionPopUp
          onClose={(refresh: boolean) => onClose(refresh)}
          isEdit={true}
          subscriptionId={subscriptionId()}
          selectedContact={subscriptionStore.detailPage.detail.contact}
          subscriptionData={new SubscriptionModel()}
          countriesWithState={itemStore.countryStore.countriesWithState}
        />
      )}
      {showCancelPopUp && (
        <CancelSubscriptionPopUp
          subscriptionDetails={subscriptionStore.detailPage.detail}
          subscriptionId={subscriptionId()}
          onClose={(refresh: boolean) => onCloseCancel(refresh)}
          isCancel={true}
        />
      )}
      {showPausePopUp && (
        <CancelSubscriptionPopUp
          subscriptionDetails={subscriptionStore.detailPage.detail}
          subscriptionId={subscriptionId()}
          onClose={(refresh: boolean) => onClosePause(refresh)}
          isCancel={false}
          showInvoices={showInvoices()}
          invoicesData={subscriptionStore.detailPage.invoices}
        />
      )}
      {showResumePopUp && (
        <ResumeSubscriptionPopup
        subscriptionDetails={subscriptionStore.detailPage.detail}
          subscriptionId={subscriptionId()}
          onClose={(refresh: boolean) => onCloseResume(refresh)}
        />
      )}
    </>
  );
};

export default withRouter(observer(SubscriptionDetailMain));
